import {
  FunctionComponent,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react'
import styled, { css } from 'styled-components'
import config from '@config'
import contextSportsHeader, {
  SportsHeaderContextType,
} from '@contexts/sportsHeader'
import { TeamLogoProps } from '@widgets/Teaser/sportsHeader/types'
import { breakpoints } from '@measures/responsive'
import { desktopCSS, tabletCSS, mobileCSS } from '@measures/responsive'

interface StyledTeamLogoProps extends Pick<TeamLogoProps, 'align'> {
  hasError: boolean
}

type StyledPictureProps = Pick<
  SportsHeaderContextType,
  'teaserFlex' | 'teaserType' | 'isInMegaModule'
>

const imageWidthDesktop = {
  '6': 40,
  '4': 40,
  '3': 30,
  '2': 25,
}
const imageWidthTablet = {
  '6': 40,
  '3': 20,
}

const imageWidthMobileRegular = 30
const imageWidthMobileSmall = 20

const sourceMediaQueryDesktop = breakpoints.desktop
const sourceMediaQueryTablet = breakpoints.tablet
const sourceMediaQueryMobileRegular = breakpoints.regularMobile
const sourceMediaQueryMobileSmall = breakpoints.smallMobile

const {
  backend: { imageUrl: backendImageUrl },
} = config

const { SportsHeaderContext } = contextSportsHeader

const StyledTeamLogo = styled.div<StyledTeamLogoProps>`
  ${({ hasError }) =>
    hasError &&
    css`
      display: none;
    `};
  line-height: 0;
  ${({ align }) => `padding-${align === 'left' ? 'right' : 'left'}: 10px;`};
`

const StyledPicture = styled.picture<StyledPictureProps>`
  width: auto;
  ${({ teaserFlex, teaserType, isInMegaModule }) => {
    const teaserFlexDesktop = teaserFlex['desktop']

    const teaserFlexTablet = teaserFlex['tablet']

    let imageHeightDesktop
    let imageHeightTablet
    let imageHeightMobile

    if (
      teaserType === 'listitem' ||
      (teaserType === 'standard' && isInMegaModule)
    ) {
      imageHeightDesktop = '24px'
      imageHeightTablet = '24px'
    } else {
      if (teaserFlexDesktop === '6') {
        imageHeightDesktop = '38px'
      } else if (teaserFlexDesktop === '4') {
        imageHeightDesktop = '38px'
      } else if (teaserFlexDesktop === '3') {
        imageHeightDesktop = '28px'
      } else if (teaserFlexDesktop === '2') {
        imageHeightDesktop = '24px'
      }

      if (teaserFlexTablet === '6') {
        imageHeightTablet = '40px'
      } else if (teaserFlexTablet === '3') {
        imageHeightTablet = '20px'
      }
    }

    if (['standard', 'listitem'].includes(teaserType)) {
      imageHeightMobile = '24px'
    } else {
      imageHeightMobile = '6.667vw'
    }

    const cssDesktop = css`
      height: ${imageHeightDesktop};
      > img {
        max-height: ${imageHeightDesktop};
        max-width: calc(2 * ${imageHeightDesktop});
      }
    `

    const cssTablet = css`
      height: ${imageHeightTablet};
      > img {
        max-height: ${imageHeightTablet};
        max-width: calc(2 * ${imageHeightTablet});
      }
    `

    const cssMobile = css`
      height: ${imageHeightMobile};
      > img {
        max-height: ${imageHeightMobile};
        max-width: calc(2 * ${imageHeightMobile});
      }
    `

    return css`
      ${desktopCSS(cssDesktop)}

      ${tabletCSS(cssTablet)}

      ${mobileCSS(cssMobile)}

      > img {
        width: auto;
        height: 100%;
      }
    `
  }}
`

const TeamLogo: FunctionComponent<TeamLogoProps> = ({ url, name, align }) => {
  const { teaserFlex, teaserType, isInMegaModule } =
    useContext(SportsHeaderContext)
  const [error, setError] = useState<boolean>(false)
  const imageRef = useRef<HTMLImageElement>(null)

  const onError = useCallback(() => {
    setError(true)
  }, [])

  useEffect(() => {
    const currentImage = imageRef.current
    currentImage?.addEventListener('error', onError)
    return () => {
      currentImage?.removeEventListener('error', onError)
    }
  }, [onError])

  const basePictureSource = `${backendImageUrl}${url}?ratio=free`
  const pictureSourceDesktop = `${basePictureSource}&imwidth=${
    imageWidthDesktop[teaserFlex.desktop]
  }`
  const pictureSourceTablet = `${basePictureSource}&imwidth=${
    imageWidthTablet[teaserFlex.tablet]
  }`
  const pictureSourceMobileRegular = `${basePictureSource}&imwidth=${imageWidthMobileRegular}`
  const pictureSourceMobileSmall = `${basePictureSource}&imwidth=${imageWidthMobileSmall}`

  return (
    <StyledTeamLogo align={align} hasError={error}>
      <StyledPicture
        teaserFlex={teaserFlex}
        teaserType={teaserType}
        isInMegaModule={isInMegaModule}>
        <source
          media={sourceMediaQueryDesktop}
          srcSet={`${pictureSourceDesktop}&imdensity=1 1x, ${pictureSourceDesktop}&imdensity=2 2x, ${pictureSourceDesktop}&imdensity=3 3x`}
        />
        <source
          media={sourceMediaQueryTablet}
          srcSet={`${pictureSourceTablet}&imdensity=1 1x, ${pictureSourceTablet}&imdensity=2 2x, ${pictureSourceTablet}&imdensity=3 3x`}
        />
        <source
          media={sourceMediaQueryMobileRegular}
          srcSet={`${pictureSourceMobileRegular}&imdensity=1 1x, ${pictureSourceMobileRegular}&imdensity=2 2x, ${pictureSourceMobileRegular}&imdensity=3 3x`}
        />
        <source
          media={sourceMediaQueryMobileSmall}
          srcSet={`${pictureSourceMobileSmall}&imdensity=1 1x, ${pictureSourceMobileSmall}&imdensity=2 2x, ${pictureSourceMobileSmall}&imdensity=3 3x`}
        />
        {/* eslint-disable-next-line @next/next/no-img-element  */}
        <img ref={imageRef} alt={name} width={1} height={1} loading="lazy" />
      </StyledPicture>
    </StyledTeamLogo>
  )
}

export default TeamLogo
